import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, Button, Empty, Tooltip } from 'antd'
import { LeftOutlined, ReloadOutlined, PlusOutlined } from '@ant-design/icons'
import {
  useAdvisoryAiOrgInternalMemory,
  useAdvisoryAiOrgReports,
  useCreateAdvisoryReportMutation,
  useDeleteAdvisoryCallMutation,
  useUpdateAdvisoryAiOrgInternalMemoryMutation,
} from '@/features/advisory-ai/hooks'
import { useAuthSession } from '@/lib/session'
import type { AdvisoryAiOrgReportRow } from '@/features/advisory-ai/types'
import { useToast } from '@/contexts/ToastContext'
import { ADVISORY_ACCESS_DENIED_MESSAGE, isAdvisoryAccessDenied } from '@/features/advisory-ai/access'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { CallStatusSection, InternalMemoryModal, StatsGrid } from './components'
import { getCallStatus } from './utils'
import dayjs from 'dayjs'

export default function AdvisoryAiOrgCalls() {
  const { id: orgId = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const clientId = useAuthSession()?.currentClientId || ''
  const { addToast } = useToast()

  const reportsQuery = useAdvisoryAiOrgReports(orgId, clientId, 1, 1000)
  const internalMemoryQuery = useAdvisoryAiOrgInternalMemory(orgId, clientId)
  const createReportMutation = useCreateAdvisoryReportMutation()
  const deleteCallMutation = useDeleteAdvisoryCallMutation(orgId, clientId)
  const updateInternalMemoryMutation = useUpdateAdvisoryAiOrgInternalMemoryMutation(orgId, clientId)
  const [isMemoryModalOpen, setIsMemoryModalOpen] = useState(false)
  const [draftInternalMemory, setDraftInternalMemory] = useState('')
  const isOrgAccessDenied = isAdvisoryAccessDenied(internalMemoryQuery.error)

  const calls = reportsQuery.data?.rows || []
  const sortedCalls = useMemo(() => {
    return [...calls].sort((a, b) => {
      const aDate = dayjs(a.meetingDate ?? a.startDate)
      const bDate = dayjs(b.meetingDate ?? b.startDate)
      if (!aDate.isValid() && !bDate.isValid()) return 0
      if (!aDate.isValid()) return 1
      if (!bDate.isValid()) return -1
      return bDate.valueOf() - aDate.valueOf()
    })
  }, [calls])

  const callsByStatus = useMemo(() => {
    const needsAttention: AdvisoryAiOrgReportRow[] = []
    const inProgress: AdvisoryAiOrgReportRow[] = []
    const generating: AdvisoryAiOrgReportRow[] = []
    const ready: AdvisoryAiOrgReportRow[] = []
    sortedCalls.forEach((call) => {
      const status = getCallStatus(call)
      if (status === 'needsAttention') needsAttention.push(call)
      else if (status === 'inProgress') inProgress.push(call)
      else if (status === 'generating') generating.push(call)
      else ready.push(call)
    })
    return { needsAttention, inProgress, generating, ready }
  }, [sortedCalls])

  const thisWeekCalls = useMemo(() => {
    const now = dayjs()
    const weekEnd = now.add(7, 'day')
    return sortedCalls.filter((call) => {
      const dateStr = call.meetingDate ?? call.startDate
      if (!dateStr) return false
      const callDate = dayjs(dateStr)
      return callDate.isAfter(now) && callDate.isBefore(weekEnd)
    }).length
  }, [sortedCalls])

  /**
   * Gates meeting creation. `profileSettled` matters as much as the value: an
   * unsettled query has no data, so treating that as "no profile" would open the
   * editor seeded with '' and a Save would overwrite a profile that exists on the
   * server. Only a loaded, genuinely empty profile blocks.
   */
  const profileSettled = internalMemoryQuery.isSuccess
  const hasIndustryProfile = !!(internalMemoryQuery.data?.internalMemory || '').trim()
  const blockedOnProfile = profileSettled && !hasIndustryProfile

  const internalMemoryPreview = useMemo(() => {
    const text = (internalMemoryQuery.data?.internalMemory || '').trim()
    if (!text) return 'No industry and profile details yet'
    if (text.length <= 90) return text
    return `${text.slice(0, 90).trim()}...`
  }, [internalMemoryQuery.data?.internalMemory])

  // A user who is on none of this organisation's Xero or Additional Access records still sees
  // every meeting; the controls just explain themselves rather than acting.
  const notPermitted = () => addToast(ADVISORY_ACCESS_DENIED_MESSAGE, 'warning')

  const handleCreateReport = async () => {
    if (!clientId || !orgId) {
      addToast('No active client selected', 'warning')
      return
    }
    // Never open the editor on an unsettled query: it would seed the draft with
    // '' and a Save would wipe whatever is actually stored.
    if (!profileSettled) {
      addToast('Still loading this organisation — try again in a moment', 'warning')
      return
    }
    if (!hasIndustryProfile) {
      addToast('Add Industry and Profile for this organisation before creating a meeting', 'warning')
      openInternalMemoryModal()
      return
    }
    try {
      const response = await createReportMutation.mutateAsync({ orgId, clientId })
      navigate(`/adviceprep/org/${orgId}/report/${response.advisoryCallId}`)
    } catch (error) {
      if (isAdvisoryAccessDenied(error)) {
        notPermitted()
        return
      }
      addToast(error instanceof Error ? error.message : 'Failed to create advisory report', 'error')
    }
  }

  const onDelete = async (row: AdvisoryAiOrgReportRow) => {
    try {
      await deleteCallMutation.mutateAsync(row.advisoryCallId)
      addToast('Meeting deleted successfully', 'success')
    } catch (error) {
      if (isAdvisoryAccessDenied(error)) {
        notPermitted()
        return
      }
      addToast(error instanceof Error ? error.message : 'Failed to delete meeting', 'error')
    }
  }

  const openInternalMemoryModal = () => {
    // The read behind this card is gated too, so a denied user has nothing to edit.
    if (isOrgAccessDenied) {
      notPermitted()
      return
    }
    setDraftInternalMemory(internalMemoryQuery.data?.internalMemory || '')
    setIsMemoryModalOpen(true)
  }

  const handleSaveInternalMemory = async () => {
    if (!clientId || !orgId) return
    try {
      await updateInternalMemoryMutation.mutateAsync({
        clientId,
        orgId,
        internalMemory: draftInternalMemory,
      })
      addToast('Industry and profile updated', 'success')
      setIsMemoryModalOpen(false)
      await internalMemoryQuery.refetch()
    } catch (error) {
      if (isAdvisoryAccessDenied(error)) {
        notPermitted()
        return
      }
      addToast(error instanceof Error ? error.message : 'Failed to update industry and profile', 'error')
    }
  }

  if (!clientId) {
    return <Alert type="warning" showIcon message="No active client selected" />
  }

  return (
    <FeatureThemeProvider
      featureKey="advisoryIntel"
      withAntd
      className="feature-shell rounded-2xl"
    >
      <div className="feature-shell__header px-6 pt-5 pb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button icon={<LeftOutlined />} onClick={() => navigate('/adviceprep')}>
              Back
            </Button>
            <h1 className="font-display text-2xl font-bold text-gray-900">Meetings</h1>
          </div>
          <div className="flex items-center gap-2">
            <Tooltip
              title={
                isOrgAccessDenied
                  ? ADVISORY_ACCESS_DENIED_MESSAGE
                  : blockedOnProfile
                    ? 'Add Industry and Profile for this organisation first — the analysis reads from it.'
                    : ''
              }
            >
              <span>
                <Button
                  data-tour="add-meeting"
                  className="feature-button-solid"
                  icon={<PlusOutlined />}
                  loading={createReportMutation.isPending}
                  disabled={isOrgAccessDenied || blockedOnProfile}
                  onClick={handleCreateReport}
                >
                  Add meeting
                </Button>
              </span>
            </Tooltip>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => reportsQuery.refetch()}
              loading={reportsQuery.isFetching}
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {reportsQuery.error ? (
        <div className="p-4">
          <Alert
            type="error"
            showIcon
            message="Failed to load meetings"
            description={reportsQuery.error instanceof Error ? reportsQuery.error.message : 'Unknown error'}
          />
        </div>
      ) : null}

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4 mb-6">
          <StatsGrid
            thisWeekCalls={thisWeekCalls}
            needsPrepCount={callsByStatus.needsAttention.length}
            // Generating folds in here: the grid is a fixed four tiles, and these
            // meetings are under way even though they have their own section.
            inProgressCount={callsByStatus.inProgress.length + callsByStatus.generating.length}
            readyCount={callsByStatus.ready.length}
          />
          <button
            type="button"
            onClick={openInternalMemoryModal}
            data-tour="internal-memory"
            className="text-left bg-white rounded-lg border border-gray-200 p-4 h-full feature-hover-border transition-colors"
          >
            <div className="text-sm text-gray-600 mb-1">INDUSTRY AND PROFILE</div>
            <div className="text-sm text-gray-900 line-clamp-4">{internalMemoryPreview}</div>
          </button>
        </div>

        {reportsQuery.isLoading || reportsQuery.isFetching ? (
          <div className="text-center py-8">Loading meetings...</div>
        ) : (
          <div data-tour="calls-list">
            <CallStatusSection
              title="NEEDS ATTENTION"
              dotColor="bg-red-500"
              calls={callsByStatus.needsAttention}
              orgId={orgId}
              isRefreshing={reportsQuery.isFetching}
              isDeleting={deleteCallMutation.isPending}
              onRefresh={() => reportsQuery.refetch()}
              onDelete={onDelete}
              onRefreshToast={() => addToast('Meeting status refreshed', 'info')}
              onBlocked={notPermitted}
            />
            <CallStatusSection
              title="IN PROGRESS"
              dotColor="bg-orange-500"
              calls={callsByStatus.inProgress}
              orgId={orgId}
              isRefreshing={reportsQuery.isFetching}
              isDeleting={deleteCallMutation.isPending}
              onRefresh={() => reportsQuery.refetch()}
              onDelete={onDelete}
              onRefreshToast={() => addToast('Meeting status refreshed', 'info')}
              onBlocked={notPermitted}
            />
            <CallStatusSection
              title="GENERATING OUTPUTS"
              dotColor="bg-blue-500"
              calls={callsByStatus.generating}
              orgId={orgId}
              isRefreshing={reportsQuery.isFetching}
              isDeleting={deleteCallMutation.isPending}
              onRefresh={() => reportsQuery.refetch()}
              onDelete={onDelete}
              onRefreshToast={() => addToast('Meeting status refreshed', 'info')}
              onBlocked={notPermitted}
            />
            <CallStatusSection
              title="READY TO GO"
              dotColor="bg-green-500"
              calls={callsByStatus.ready}
              orgId={orgId}
              isRefreshing={reportsQuery.isFetching}
              isDeleting={deleteCallMutation.isPending}
              onRefresh={() => reportsQuery.refetch()}
              onDelete={onDelete}
              onRefreshToast={() => addToast('Meeting status refreshed', 'info')}
              onBlocked={notPermitted}
            />

            {calls.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Empty description="No meetings scheduled" />
              </div>
            )}
          </div>
        )}
      </div>
      <InternalMemoryModal
        open={isMemoryModalOpen}
        value={draftInternalMemory}
        isSaving={updateInternalMemoryMutation.isPending}
        onChange={setDraftInternalMemory}
        onCancel={() => setIsMemoryModalOpen(false)}
        onSave={handleSaveInternalMemory}
      />
    </FeatureThemeProvider>
  )
}
